import { SupportedLocale } from "@/i18n";
import { DimensionsFormatting } from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import { DimensionsDTO } from "@/lib/shared-types";
import { parseJsonOrUndefined } from "@/lib/utils";

interface ArtworkTirageInfoProps {
  tirageData: any;
  locale: SupportedLocale;
  t: (key: string) => string;
}

export function ArtworkTirageInfo({
  tirageData,
  locale,
  t,
}: ArtworkTirageInfoProps) {
  if (!tirageData) return null;

  const dimensionsWithMargins =
    tirageData.tirageDescriptionDimensionsWithMargins
      ? typeof tirageData.tirageDescriptionDimensionsWithMargins === "string"
        ? parseJsonOrUndefined<DimensionsDTO>(
            tirageData.tirageDescriptionDimensionsWithMargins,
          )
        : tirageData.tirageDescriptionDimensionsWithMargins
      : undefined;

  const dimensionsWithoutMargins =
    tirageData.tirageDescriptionDimensionsWithoutMargins
      ? typeof tirageData.tirageDescriptionDimensionsWithoutMargins === "string"
        ? parseJsonOrUndefined<DimensionsDTO>(
            tirageData.tirageDescriptionDimensionsWithoutMargins,
          )
        : tirageData.tirageDescriptionDimensionsWithoutMargins
      : undefined;

  return (
    <div className="my-1 mt-4 flex flex-col gap-2 font-normal front-sans text-sm leading-[110%] subpixel-antialiased">
      {/* Dimensions avec marges */}
      {dimensionsWithMargins && (
        <p>
          {t("singleArtworkSubtitleTirageDimensionsWithMargins") + ": "}
          <DimensionsFormatting
            input={dimensionsWithMargins}
            language={locale}
            lineBreak={false}
          />
        </p>
      )}
      {/* Dimensions sans marges */}
      {dimensionsWithoutMargins && (
        <p>
          {t("singleArtworkSubtitleTirageDimensionsWithoutMargins") + ": "}
          <DimensionsFormatting
            input={dimensionsWithoutMargins}
            language={locale}
            lineBreak={false}
          />
        </p>
      )}
      {/* Technique sur Support */}
      {(tirageData.tirageDescriptionTechnique?.[locale] ||
        tirageData.tirageDescriptionSupport?.[locale]) && (
        <p>
          {t("singleArtworkSubtitleTirageTechniqueSupport") + ": "}
          <SupportTechniqueCF
            input={{
              technique: tirageData.tirageDescriptionTechnique || {},
              support: tirageData.tirageDescriptionSupport || {},
              preposition: t("computeField.techniqueSupportPreposition"),
            }}
            language={locale}
            lineBreak={false}
          />
        </p>
      )}
      {/* Type de tirage */}
      {tirageData.tirageDescriptionType?.[locale] && (
        <p>
          {t("singleArtworkSubtitleTirageType") + ": "}
          {tirageData.tirageDescriptionType[locale]}
        </p>
      )}
      {/* Date du tirage */}
      {tirageData.tirageDescriptionDateCreation && (
        <p>
          {t("singleArtworkSubtitleTirageDate") + ": "}
          {tirageData.tirageDescriptionDateCreation}
        </p>
      )}
      {/* Tireur */}
      {tirageData.tirageDescriptionPrinter?.[locale] && (
        <p>
          {t("singleArtworkSubtitleTiragePrinter") + ": "}
          {tirageData.tirageDescriptionPrinter[locale]}
        </p>
      )}
      {/* Description de la signature */}
      {tirageData.tirageDescriptionSignatureInscription?.[locale] && (
        <p>
          {t("singleArtworkSubtitleTirageSignature") + ": "}
          {tirageData.tirageDescriptionSignatureInscription[locale]}
        </p>
      )}
      {/* Description de la signature du dos */}
      {tirageData.tirageDescriptionSignatureInscriptionDos?.[locale] &&
        tirageData.tirageDescriptionSignatureInscriptionDosPublished?.[
          locale
        ] && (
          <p>
            {t("singleArtworkSubtitleTirageSignatureDos") + ": "}
            {tirageData.tirageDescriptionSignatureInscriptionDos[locale]}
          </p>
        )}
      {/* Édition / n° */}
      {tirageData.tirageDescriptionEditionNumber?.[locale] && (
        <p>
          {t("singleArtworkSubtitleTirageEdition") + ": "}
          {tirageData.tirageDescriptionEditionNumber[locale]}
        </p>
      )}
    </div>
  );
}
